import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { axiosSalary, axiosMaster } from "../api/axiosInstance";
import Select from "react-select";
import { FaSearch, FaFileExcel, FaExclamationTriangle } from "react-icons/fa";
import { useAuth } from "../auth/AuthContext";
import AddSessionByManager from "./AddSessionByManager";
import ReplaceSessionByManager from "./ReplaceSessionByManager";
import LockAttendance from "./LockAttendance";
import UnlockAttendance from "./UnlockAttendance";
import RemoveSessionByManager from "./RemoveSessionByManager";
import FinalizeAttendance from "./FinalizeAttendance";
import UnfinalizeAttendance from "./UnfinalizeAttendance";
import EditSessionByManager from "./EditSessionByManager";

/* ═══════════════════════════════════════════════════
   DATE UTILITY — YYYYMMDD integer, no timezone issues
   Handles:
     "2026-06-17T00:00:00Z"  (ISO with time)
     "2026-06-17"            (ISO date only)
     "17/06/2026"            (DD/MM/YYYY)
     "17-06-2026"            (DD-MM-YYYY)
   ═══════════════════════════════════════════════════ */
const toDateInt = (str) => {
  if (!str) return null;
  const s = String(str).trim();

  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    return parseInt(s.slice(0, 10).replace(/-/g, ""), 10);
  }
  if (/^\d{2}\/\d{2}\/\d{4}/.test(s)) {
    const [dd, mm, yyyy] = s.split("/");
    return parseInt(`${yyyy}${mm}${dd}`, 10);
  }
  if (/^\d{2}-\d{2}-\d{4}/.test(s)) {
    const [dd, mm, yyyy] = s.split("-");
    return parseInt(`${yyyy}${mm}${dd}`, 10);
  }
  return null;
};

/* ═══════════════════════════════════════════════════
   isActiveOnDate
   Business Rule (confirmed for this system):
     relievingDate = null       → currently working → show ✅
     relievingDate has any date → employee left     → hide ❌
   (Same simple rule used across all other reports in this app.)
   NOTE: kept exactly as originally specified. If you want an employee
   who left AFTER the report's date-range to still appear in that
   historical range, this rule needs a date-range comparison instead —
   flagging this here, not changing behavior without confirmation.
   ═══════════════════════════════════════════════════ */
const isActiveOnDate = (emp) => {
  if (!emp) return true; // no master record found → don't hide, fail-safe

  const tenures =
    Array.isArray(emp.employmentHistory) && emp.employmentHistory.length > 0
      ? emp.employmentHistory
      : [{ relievingDate: emp.relievingDate ?? null }];

  return tenures.some((tenure) => !toDateInt(tenure.relievingDate));
};

const AttendanceExceptionReport = () => {
  /* ─── AUTH ─── */
  const { user } = useAuth();
  const groupId = user?.groupId;
  const userRestId = user?.restaurantId;

  /* ─── STATE ─── */
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(null); // distinct from "no data"

  // Employee master map for relievingDate lookups (empId → employee)
  const [empMasterMap, setEmpMasterMap] = useState({});
  const [masterError, setMasterError] = useState(null);

  const [sessionOpen, setSessionOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [replaceOpen, setReplaceOpen] = useState(false);
  const [selectedReplaceRow, setSelectedReplaceRow] = useState(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [selectedLockRow, setSelectedLockRow] = useState(null);
  const [unlockOpen, setUnlockOpen] = useState(false);
  const [selectedUnlockRow, setSelectedUnlockRow] = useState(null);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [selectedRemoveRow, setSelectedRemoveRow] = useState(null);

  const [restaurantList, setRestaurantList] = useState([]);
  const [selectedRestaurant, setSelectedRestaurant] = useState(null);
  const [restaurantError, setRestaurantError] = useState(null);

  const [finalizeOpen, setFinalizeOpen] = useState(false);
  const [selectedFinalizeRow, setSelectedFinalizeRow] = useState(null);
  const [unfinalizeOpen, setUnfinalizeOpen] = useState(false);
  const [selectedUnfinalizeRow, setSelectedUnfinalizeRow] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedEditRow, setSelectedEditRow] = useState(null);

  // A short-lived banner shown after a modal action succeeds and the
  // report has been refreshed, so it's obvious the table actually updated.
  const [refreshNotice, setRefreshNotice] = useState(null);

  /* ─── RACE-CONDITION GUARD ───────────────────────────────────────────
     Every fetchReport() call gets an incrementing token. When a response
     comes back, we only apply it if it's still the latest request in
     flight. This stops a slow, stale request (e.g. from the restaurant
     you just switched away from) from overwriting fresher data.
  ─────────────────────────────────────────────────────────────────── */
  const requestIdRef = useRef(0);

  /* ─── FETCH EMPLOYEE MASTER (once, used for relievingDate filtering) ─── */
  const fetchEmployeeMaster = useCallback(async () => {
    if (!groupId) return;
    try {
      setMasterError(null);
      const res = await axiosMaster.get(`/EmployeeMaster/GetByGroup?groupId=${groupId}`);
      const map = {};
      (res.data || []).forEach((e) => {
        map[e.empId] = e;
      });
      setEmpMasterMap(map);
    } catch (err) {
      console.error("Employee master fetch error:", err);
      setMasterError("Could not load employee master data. Relieved-employee filtering may be inaccurate.");
    }
  }, [groupId]);

  useEffect(() => {
    fetchEmployeeMaster();
  }, [fetchEmployeeMaster]);

  /* ─── FETCH RESTAURANTS ─── */
  const fetchRestaurants = useCallback(async () => {
    if (!groupId) return;
    try {
      setRestaurantError(null);
      const res = await axiosSalary.get(`/RestaurantMaster/GetByGroup?groupId=${groupId}`);

      const options = (res.data || []).map((r) => ({
        value: r.restId,
        label: r.restaurantName,
      }));

      setRestaurantList(options);

      // Prefer: (1) the logged-in user's own assigned restaurant,
      // (2) whatever was last picked (localStorage), (3) first in list.
      const savedRestId = localStorage.getItem("restaurantId");
      const defaultRest =
        options.find((o) => String(o.value) === String(userRestId)) ||
        options.find((o) => String(o.value) === String(savedRestId)) ||
        options[0] ||
        null;

      setSelectedRestaurant(defaultRest);
    } catch (err) {
      console.error("Restaurant fetch error:", err);
      setRestaurantError("Could not load restaurant list. Please refresh the page.");
    }
  }, [groupId, userRestId]);

  useEffect(() => {
    fetchRestaurants();
  }, [fetchRestaurants]);

  /* ─── DEFAULT DATE (today) ─── */
  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    setFromDate(today);
    setToDate(today);
  }, []);

  /* ─── FETCH EXCEPTION REPORT ───────────────────────────────────────
     Single source of truth for loading the report. Only called from
     the effect below, so restaurant/date changes never trigger a
     duplicate simultaneous request.
  ─────────────────────────────────────────────────────────────────── */
  const fetchReport = useCallback(async () => {
    const restIdParam = selectedRestaurant?.value;
    if (!fromDate || !toDate || !restIdParam || !groupId) return;

    const myRequestId = ++requestIdRef.current;

    try {
      setLoading(true);
      setFetchError(null);

      const res = await axiosSalary.get(
        `/DailyAttendance/GetExceptionReport?groupId=${groupId}&restId=${restIdParam}&fromDate=${fromDate}&toDate=${toDate}`
      );

      // Ignore this response if a newer request has since been fired
      // (e.g. user switched restaurant/date again before this returned).
      if (myRequestId !== requestIdRef.current) return;

      setData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      if (myRequestId !== requestIdRef.current) return;
      console.error("Exception report error:", err);
      setData([]);
      setFetchError(
        err?.response?.status === 401 || err?.response?.status === 403
          ? "Session expired or not authorized. Please log in again."
          : "Could not load the report. Please check your connection and try again."
      );
    } finally {
      if (myRequestId === requestIdRef.current) setLoading(false);
    }
  }, [fromDate, toDate, groupId, selectedRestaurant?.value]);

  // Single effect drives every report fetch — restaurant change, date
  // change, or an explicit refresh() call after a modal action.
  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleRestaurantChange = (selected) => {
    setSelectedRestaurant(selected);
    if (selected?.value) {
      localStorage.setItem("restaurantId", selected.value);
    }
    // No manual fetchReport() call here — the useEffect above reacts to
    // selectedRestaurant?.value changing, so we avoid firing it twice.
  };

  /* ─── REFRESH AFTER A MODAL ACTION SUCCEEDS ───────────────────────
     Shared by every modal's onSuccess. Re-runs the report fetch and
     shows a brief confirmation banner so it's obvious the table
     actually reflects the change (rather than silently doing nothing
     if the refetch happens to fail).
  ─────────────────────────────────────────────────────────────────── */
  const refreshAfterAction = useCallback(
    async (label = "Change") => {
      await fetchReport();
      setRefreshNotice(`${label} saved and table refreshed.`);
      window.clearTimeout(refreshAfterAction._t);
      refreshAfterAction._t = window.setTimeout(() => setRefreshNotice(null), 3500);
    },
    [fetchReport]
  );

  /* ─── OPEN MODAL HANDLERS ─── */
  const openSessionModal = (row) => {
    setSelectedRow(row);
    setSessionOpen(true);
  };
  const openReplaceModal = (row) => {
    setSelectedReplaceRow(row);
    setReplaceOpen(true);
  };
  const openLockModal = (row) => {
    setSelectedLockRow(row);
    setLockOpen(true);
  };
  const openUnlockModal = (row) => {
    setSelectedUnlockRow(row);
    setUnlockOpen(true);
  };
  const openRemoveModal = (row) => {
    setSelectedRemoveRow(row);
    setRemoveOpen(true);
  };
  const openFinalizeModal = (row) => {
    setSelectedFinalizeRow(row);
    setFinalizeOpen(true);
  };
  const openUnfinalizeModal = (row) => {
    setSelectedUnfinalizeRow(row);
    setUnfinalizeOpen(true);
  };
  const openEditModal = (row) => {
    setSelectedEditRow(row);
    setEditOpen(true);
  };

  /* ─── FILTER ─────────────────────────────────────────────────────────
     1. Relieved-employee filter: hide rows where the employee's master
        record has a relievingDate (regardless of the row's own date).
     2. Search filter on name/id/remarks.
  ─────────────────────────────────────────────────────────────────── */
  const filteredData = useMemo(() => {
    const term = search.toLowerCase();

    return data.filter((d) => {
      const emp = empMasterMap[d.empId];
      if (!isActiveOnDate(emp)) return false;

      return (
        d.empName?.toLowerCase().includes(term) ||
        String(d.empId).includes(term) ||
        d.remarks?.toLowerCase().includes(term)
      );
    });
  }, [data, search, empMasterMap]);

  /* ─── SUMMARY ─── */
  const summary = useMemo(() => {
    let late = 0,
      earlyLeave = 0,
      halfDay = 0,
      suspense = 0,
      absent = 0;

    filteredData.forEach((d) => {
      if (d.isAbsent) absent++;
      if (d.isLate) late++;
      if (d.isEarlyLeaving) earlyLeave++;
      if (d.isHalfDay) halfDay++;
      if (d.isSuspense) suspense++;
    });

    return { total: filteredData.length, late, earlyLeave, halfDay, suspense, absent };
  }, [filteredData]);

  /* ─── CSV ESCAPE HELPER ─── */
  const escapeCSV = (value) => {
    if (value === null || value === undefined) return "";
    const str = String(value);
    const safe = /^[=+\-@]/.test(str) ? `'${str}` : str;
    if (safe.includes(",") || safe.includes('"') || safe.includes("\n")) {
      return `"${safe.replace(/"/g, '""')}"`;
    }
    return safe;
  };

  /* ─── STATUS ─── */
  const getStatusText = (row) => {
    if (row.isAbsent) return "Absent";
    if (row.isSuspense) return "Suspense";
    if (row.isHalfDay) return "Half Day";
    if (row.isEarlyLeaving) return "Early Leave";
    if (row.isLate) return "Late";
    return "Present";
  };

  const getStatusBadge = (row) => {
    if (row.isSuspense)
      return <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded text-xs">Suspense</span>;
    if (row.isHalfDay)
      return <span className="bg-orange-100 text-orange-700 px-2 py-1 rounded text-xs">Half Day</span>;
    if (row.isEarlyLeaving)
      return <span className="bg-red-100 text-red-700 px-2 py-1 rounded text-xs">Early Leave</span>;
    if (row.isLate)
      return <span className="bg-yellow-100 text-yellow-700 px-2 py-1 rounded text-xs">Late</span>;
    return <span className="bg-gray-100 text-gray-600 px-2 py-1 rounded text-xs">Normal</span>;
  };

  /* ─── EXPORT ─── */
  const exportExcel = () => {
    if (!filteredData.length) return;

    const rows = filteredData.map((r) => ({
      EmpId: r.empId,
      Name: r.empName || "",
      Date: r.attendanceDate,
      FirstPunch: r.firstPunchIst ? `\t${r.firstPunchIst}` : "",
      LastPunch: r.lastPunchIst ? `\t${r.lastPunchIst}` : "",
      Punches: r.totalPunches,
      WorkingMinutes: r.workingMinutes,
      LateMinutes: r.lateMinutes || 0,
      EarlyLeaveMinutes: r.earlyLeaveMinutes || 0,
      OvertimeMinutes: r.overtimeMinutes,
      Status: getStatusText(r),
      Remarks: r.remarks || "",
    }));

    const csv =
      "data:text/csv;charset=utf-8," +
      [
        Object.keys(rows[0]).join(","),
        ...rows.map((row) => Object.values(row).map(escapeCSV).join(",")),
      ].join("\n");

    const link = document.createElement("a");
    link.href = encodeURI(csv);
    link.download = `ExceptionReport_${fromDate}_to_${toDate}.csv`;
    link.click();
  };

  return (
    <div className="bg-white p-4 md:p-5 rounded-xl shadow border">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row justify-between gap-3 mb-3">
        <h2 className="text-lg font-semibold">Attendance Exception Report</h2>

        <div className="flex flex-wrap gap-2 items-center ml-auto">
          <Select
            value={selectedRestaurant}
            onChange={handleRestaurantChange}
            options={restaurantList}
            placeholder="Select Restaurant"
            className="text-sm min-w-[180px]"
            isDisabled={!restaurantList.length}
          />

          <div className="relative">
            <FaSearch className="absolute left-2 top-2 text-gray-400 text-xs" />
            <input
              type="text"
              placeholder="Search employee..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-7 pr-2 py-1.5 border rounded text-sm"
            />
          </div>

          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-2 py-1 rounded text-sm"
          />

          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-2 py-1 rounded text-sm"
          />

          <button
            onClick={exportExcel}
            disabled={!filteredData.length}
            className="bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed text-white px-3 py-1.5 rounded text-sm flex items-center gap-2"
          >
            <FaFileExcel />
            Export
          </button>
        </div>
      </div>

      {/* NOTICES */}
      {refreshNotice && (
        <div className="mb-3 text-sm bg-green-50 text-green-700 border border-green-200 rounded px-3 py-2">
          {refreshNotice}
        </div>
      )}

      {restaurantError && (
        <div className="mb-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded px-3 py-2 flex items-center gap-2">
          <FaExclamationTriangle /> {restaurantError}
        </div>
      )}

      {masterError && (
        <div className="mb-3 text-sm bg-yellow-50 text-yellow-700 border border-yellow-200 rounded px-3 py-2 flex items-center gap-2">
          <FaExclamationTriangle /> {masterError}
        </div>
      )}

      {fetchError && (
        <div className="mb-3 text-sm bg-red-50 text-red-700 border border-red-200 rounded px-3 py-2 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <FaExclamationTriangle /> {fetchError}
          </span>
          <button
            onClick={() => fetchReport()}
            className="bg-red-600 text-white px-2 py-1 rounded text-xs hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      )}

      {/* SUMMARY */}
      <div className="grid grid-cols-6 gap-2 text-xs md:text-sm mb-3">
        <div className="bg-gray-100 p-2 rounded text-center">
          <p>Total</p>
          <p>{summary.total}</p>
        </div>
        <div className="bg-yellow-100 p-2 rounded text-center">
          <p>Late</p>
          <p>{summary.late}</p>
        </div>
        <div className="bg-red-100 p-2 rounded text-center">
          <p>Early Leave</p>
          <p>{summary.earlyLeave}</p>
        </div>
        <div className="bg-orange-100 p-2 rounded text-center">
          <p>Half Day</p>
          <p>{summary.halfDay}</p>
        </div>
        <div className="bg-purple-100 p-2 rounded text-center">
          <p>Suspense</p>
          <p>{summary.suspense}</p>
        </div>
        <div className="bg-gray-300 p-2 rounded text-center">
          <p>Absent</p>
          <p>{summary.absent}</p>
        </div>
      </div>

      {/* DESKTOP */}
      <div className="hidden md:block border rounded-lg max-h-[500px] overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-gray-200 sticky top-0">
            <tr>
              <th className="border p-2">ID</th>
              <th className="border p-2">Name</th>
              <th className="border p-2">Date</th>
              <th className="border p-2">First Punch</th>
              <th className="border p-2">Last Punch</th>
              <th className="border p-2">Punch</th>
              <th className="border p-2">Work Min</th>
              <th className="border p-2">OT Min</th>
              <th className="border p-2">Status</th>
              <th className="border p-2">Remarks</th>
              <th className="border p-2">Action</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="11" className="text-center p-4">
                  Loading...
                </td>
              </tr>
            ) : fetchError ? (
              <tr>
                <td colSpan="11" className="text-center p-4 text-red-500">
                  Could not load data — see message above.
                </td>
              </tr>
            ) : filteredData.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center p-4 text-gray-400">
                  No data for this restaurant / date range.
                </td>
              </tr>
            ) : (
              filteredData.map((row) => (
                <tr key={row.empId + row.attendanceDate}>
                  <td className="border p-2">{row.empId}</td>
                  <td className="border p-2">{row.empName}</td>
                  <td className="border p-2">{row.attendanceDate}</td>
                  <td className="border p-2">{row.firstPunchIst || "-"}</td>
                  <td className="border p-2">{row.lastPunchIst || "-"}</td>

                  <td className="border p-2 text-center">
                    <span
                      className={`px-2 py-1 rounded text-xs ${
                        row.totalPunches % 2 !== 0
                          ? "bg-red-100 text-red-700"
                          : "bg-green-100 text-green-700"
                      }`}
                    >
                      {row.totalPunches}
                    </span>
                  </td>

                  <td className="border p-2">{row.workingMinutes}</td>
                  <td className="border p-2 text-blue-600">{row.overtimeMinutes}</td>

                  <td className="border p-2">{getStatusBadge(row)}</td>

                  <td className="border p-2 text-xs">
                    {row.remarks}
                    {row.isManualEdited && <div className="text-orange-500">Edited</div>}
                  </td>

                  <td className="border p-2 text-center">
                    <div className="flex gap-1 justify-center">
                      <button
                        onClick={() => openEditModal(row)}
                        className="bg-indigo-600 text-white px-2 py-1 rounded text-xs"
                      >
                        Edit Session
                      </button>

                      {row.isAbsent ? (
                        <button
                          onClick={() => openSessionModal(row)}
                          className="bg-blue-600 text-white px-2 py-1 rounded text-xs"
                        >
                          + Session
                        </button>
                      ) : (
                        <>
                          {row.totalPunches % 2 !== 0 && (
                            <button
                              onClick={() => openReplaceModal(row)}
                              className="bg-orange-600 text-white px-2 py-1 rounded text-xs"
                            >
                              Replace
                            </button>
                          )}

                          <button
                            onClick={() => openRemoveModal(row)}
                            className="bg-red-500 text-white px-2 py-1 rounded text-xs"
                          >
                            Remove Session
                          </button>

                          {row.isLocked ? (
                            <button
                              onClick={() => openUnlockModal(row)}
                              className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                            >
                              Unlock
                            </button>
                          ) : (
                            <button
                              onClick={() => openLockModal(row)}
                              className="bg-red-600 text-white px-2 py-1 rounded text-xs"
                            >
                              Lock
                            </button>
                          )}
                        </>
                      )}

                      {row.isFinalized ? (
                        <button
                          onClick={() => openUnfinalizeModal(row)}
                          className="bg-green-600 text-white px-2 py-1 rounded text-xs"
                        >
                          Unfinalize
                        </button>
                      ) : (
                        <button
                          onClick={() => openFinalizeModal(row)}
                          className="bg-blue-700 text-white px-2 py-1 rounded text-xs"
                        >
                          Finalize
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* MOBILE */}
      <div className="md:hidden space-y-3 mt-3">
        {loading ? (
          <div className="text-center p-4">Loading...</div>
        ) : fetchError ? (
          <div className="text-center p-4 text-red-500">Could not load data — see message above.</div>
        ) : filteredData.length === 0 ? (
          <div className="text-center p-4 text-gray-400">No data for this restaurant / date range.</div>
        ) : (
          filteredData.map((row) => (
            <div key={row.empId + row.attendanceDate} className="border p-3 rounded-xl shadow-sm bg-white">
              <div className="flex justify-between items-center mb-1">
                <h3 className="font-semibold text-sm">{row.empName}</h3>
                {getStatusBadge(row)}
              </div>

              <div className="text-xs text-gray-600">
                <div>ID: {row.empId}</div>
                <div>Date: {row.attendanceDate}</div>
              </div>

              <div className="mt-2 text-xs">
                <div>First Punch: {row.firstPunchIst || "-"}</div>
                <div>Last Punch: {row.lastPunchIst || "-"}</div>
              </div>

              <div className="mt-2">
                <span
                  className={`px-2 py-1 rounded text-xs ${
                    row.totalPunches % 2 !== 0 ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                  }`}
                >
                  Punch: {row.totalPunches}
                </span>
              </div>

              <div className="mt-2 text-xs flex justify-between">
                <span>Work: {row.workingMinutes}</span>
                <span className="text-blue-600">OT: {row.overtimeMinutes}</span>
              </div>

              <div className="mt-2 text-xs text-gray-700">
                {row.remarks}
                {row.isManualEdited && <div className="text-orange-500">Edited</div>}
              </div>

              <div className="mt-3 space-y-1">
                <button
                  onClick={() => openEditModal(row)}
                  className="w-full bg-indigo-600 text-white py-1 rounded text-xs"
                >
                  Edit Session
                </button>

                {row.isAbsent ? (
                  <button
                    onClick={() => openSessionModal(row)}
                    className="w-full bg-blue-600 text-white py-1 rounded text-xs"
                  >
                    + Add Session
                  </button>
                ) : (
                  <>
                    {row.totalPunches % 2 !== 0 && (
                      <button
                        onClick={() => openReplaceModal(row)}
                        className="w-full bg-orange-600 text-white py-1 rounded text-xs"
                      >
                        Replace Session
                      </button>
                    )}

                    <button
                      onClick={() => openRemoveModal(row)}
                      className="w-full bg-red-500 text-white py-1 rounded text-xs"
                    >
                      Remove Session
                    </button>

                    {row.isLocked ? (
                      <button
                        onClick={() => openUnlockModal(row)}
                        className="w-full bg-green-600 text-white py-1 rounded text-xs"
                      >
                        Unlock Attendance
                      </button>
                    ) : (
                      <button
                        onClick={() => openLockModal(row)}
                        className="w-full bg-red-600 text-white py-1 rounded text-xs"
                      >
                        Lock Attendance
                      </button>
                    )}
                  </>
                )}

                {row.isFinalized ? (
                  <button
                    onClick={() => openUnfinalizeModal(row)}
                    className="w-full bg-green-600 text-white py-1 rounded text-xs"
                  >
                    Unfinalize
                  </button>
                ) : (
                  <button
                    onClick={() => openFinalizeModal(row)}
                    className="w-full bg-blue-700 text-white py-1 rounded text-xs"
                  >
                    Finalize
                  </button>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* MODALS — every onSuccess now goes through refreshAfterAction so
          a visible confirmation banner appears once the table has
          actually re-synced, instead of assuming it silently worked. */}
      <AddSessionByManager
        open={sessionOpen}
        onClose={() => setSessionOpen(false)}
        rowData={selectedRow}
        onSuccess={() => refreshAfterAction("Session")}
      />

      <ReplaceSessionByManager
        open={replaceOpen}
        onClose={() => setReplaceOpen(false)}
        rowData={selectedReplaceRow}
        onSuccess={() => refreshAfterAction("Session replacement")}
      />

      <LockAttendance
        open={lockOpen}
        onClose={() => setLockOpen(false)}
        rowData={selectedLockRow}
        onSuccess={() => refreshAfterAction("Lock")}
      />

      <UnlockAttendance
        open={unlockOpen}
        onClose={() => setUnlockOpen(false)}
        rowData={selectedUnlockRow}
        onSuccess={() => refreshAfterAction("Unlock")}
      />

      <RemoveSessionByManager
        open={removeOpen}
        onClose={() => setRemoveOpen(false)}
        rowData={selectedRemoveRow}
        onSuccess={() => refreshAfterAction("Session removal")}
      />

      <FinalizeAttendance
        open={finalizeOpen}
        onClose={() => setFinalizeOpen(false)}
        rowData={selectedFinalizeRow}
        onSuccess={() => refreshAfterAction("Finalize")}
      />

      <UnfinalizeAttendance
        open={unfinalizeOpen}
        onClose={() => setUnfinalizeOpen(false)}
        rowData={selectedUnfinalizeRow}
        onSuccess={() => refreshAfterAction("Unfinalize")}
      />

      <EditSessionByManager
        open={editOpen}
        onClose={() => setEditOpen(false)}
        rowData={selectedEditRow}
        onSuccess={() => refreshAfterAction("Edit session")}
      />
    </div>
  );
};

export default AttendanceExceptionReport;